import * as angular from 'angular';
import * as moment from 'moment';

import {
    WidgetValues,
    ComponentDefinition,
    Dashboard,
    IValueViewModel,
    Dictionary,
    DigitalSetSimpleDictionary,
    TagStringValueDto,
    AggregationMode
} from '../../models';
import { ITileWidgetService, IDashboardLoadService, IDateRangeService, IExportToCsvService } from '../../services';
import { isEditMode } from '../../common';
import { IRootScope } from 'IRootScope';
import { ITileViewModel } from '@services/data/ITileViewModel';
import { TileWidget } from './tileWidget.widget';

class TileWidgetController implements angular.IComponentController {
    data: ITileViewModel[];
    isEditMode: boolean = false;
    isLoading: boolean;

    readonly boundData: WidgetValues;
    readonly dashboards: Dashboard[];
    readonly digitalSets: DigitalSetSimpleDictionary;
    readonly item: TileWidget;
    readonly series: any[];
    readonly stringValues: TagStringValueDto[];
    readonly viewModels: Dictionary<IValueViewModel>;

    private _previousWatcher: () => void;

    static $inject = ['$scope', '$rootScope', '$timeout', '$element', 'tileWidgetService', 'dashboardLoadService', 'dateRangeService', 'exportToCsvService'];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$timeout: angular.ITimeoutService,
        private _$element: angular.IAugmentedJQuery,
        private _tileWidgetService: ITileWidgetService,
        private _dashboardLoadService: IDashboardLoadService,
        private _dateRangeService: IDateRangeService,
        private _exportToCsvService: IExportToCsvService
    ) {}

    $onInit() {
        this.isEditMode = this._$rootScope.isEditMode;
        this._$scope.$on(isEditMode, () => (this.isEditMode = this._$rootScope.isEditMode));
        this.data = [];
        // ensure first render of html
        this.refresh();
        this._$element.on('resize', () => this.refresh());
        if (this.item) {
            this.item.exportToCsv = () => {
                const table = this._tileWidgetService.buildCsvTable(this.item.configuration, this.boundData, this.viewModels, this.digitalSets, this.stringValues);
                return this._exportToCsvService.getCsvFromData(table, `${this.item.name}_${moment().format('YYYY-MM-DD HHmm')}.csv`);
            };
        }
    }

    /**
     * Event method called when a change is detected
     * @param changes Object containing changed data
     */
    $onChanges(changes: angular.IOnChangesObject) {
        if (changes['item']?.currentValue) {
            // Add deep watch for refresh if series changed
            if (this._previousWatcher) {
                this._previousWatcher();
            }

            this._previousWatcher = this._$scope.$watch(
                () => this.item.configuration,
                () => this.refresh(),
                true
            );
        }

        if (changes['boundData']?.currentValue?.guid) {
            // ensure html is already rendered for dynamic height
            this._$timeout(() => this.refresh());
        }
    }

    private getHeight(): number {
        const element = this._$element.find('.tile-widget-container');
        const height = element.height();
        return height;
    }

    private refresh(): void {
        this._$timeout(() => {
            if (this.boundData) {
                this.data = this._tileWidgetService.getTileViewModels(
                    (this.item as any).series ?? this.item.configuration.series,
                    this.boundData,
                    this.viewModels,
                    this.digitalSets,
                    { aggregationMode: this.item.configuration.aggregationMode || AggregationMode.Average },
                    this.getHeight(),
                    this.getRelativeWidth(),
                    this.stringValues
                );
            } else {
                this.readData();
            }
        });
    }

    // Get width defined by user (Use it to adjust component font size)
    private getRelativeWidth(): number {
        const serieCount = this.item.configuration.series ? this.item.configuration.series.length : 1;
        return (1 / Math.max(serieCount, 1)) * 100;
    }

    // Display data in edit mode when selection is "value"
    private readData(): void {
        if (!this.boundData) {
            const beginDate = this._dateRangeService.beginDate() as string;
            const endDate = this._dateRangeService.endDate() as string;
            const config = [this.item.getReadConfiguration(beginDate, endDate)];

            this.isLoading = true;
            this._dashboardLoadService
                .readValues(config, beginDate, endDate)
                .then((widgetValues) => {
                    if (widgetValues && widgetValues.length > 0) {
                        this.data = this._tileWidgetService.getTileViewModels(
                            this.item.configuration.series,
                            widgetValues[0],
                            this.viewModels,
                            this.digitalSets,
                            { aggregationMode: this.item.configuration.aggregationMode || AggregationMode.Average },
                            this.getHeight(),
                            this.getRelativeWidth(),
                            this.stringValues
                        );
                    }
                })
                .catch((reason) => console.log(reason))
                .finally(() => (this.isLoading = false));
        }
    }
}

export const wtTileWidget: ComponentDefinition = {
    componentId: 'wtTileWidget',
    controller: TileWidgetController,
    template: require('./tileWidget.component.html'),
    controllerAs: 'vm',
    bindings: {
        boundData: '<?',
        dashboards: '<',
        digitalSets: '<',
        item: '<',
        series: '<',
        stringValues: '<',
        viewModels: '<'
    }
};
